import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Modal,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@mui/material';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import Button from 'components/Common/Button';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import Select, { Option } from 'components/Common/Select';
import Input from 'components/Common/Input';
import { useCreateOrUpdateAgentAction, useGetAgentAction } from 'apis/business/agent/action/hook';
import { FaPlus } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import Spinner from 'components/Common/Spinner';
import { AgentActionRequest } from 'apis/business/agent/action/types';
import { useGetOperators } from 'apis/business/setting/operator/hook';
import { useEffect, useMemo, useState } from 'react';
import { useGetAvailableIntegrations, useGetIntegrations } from 'apis/business/setting/integrations/hook';
import Radio from 'components/Common/Radio';
import IntegrationForm from 'containers/Business/Setting/Integrations/Components/IntegrationForm';
import OperatorForm from 'containers/Business/Setting/Operator/Components/OperatorForm';
import * as urls from 'routes/config';
import { zodResolver } from '@hookform/resolvers/zod';
import { ActionFormSchema } from 'utils/schema/validator/ActionFormValidation';
import { IoChevronDownOutline } from 'react-icons/io5';
import Switch from 'components/Common/Switch';
import { useGetWorksheets } from 'apis/business/agent/worksheet/hook';
import { GoPlus } from 'react-icons/go';
import { useGetAgentTypeOptions } from 'apis/business/agent/agentActions/hook';
import { a11yProps } from 'components/Common/CustomTabPanel';
import DataTable from 'components/Common/DataTable';
import TimePicker from 'components/Common/TimePicker';
import { ColumnDef } from '@tanstack/react-table';
import { IoMdInformationCircleOutline } from 'react-icons/io';

export default function ActionForm() {
  const navigate = useNavigate();
  const { workspaceId, agentId, actionId } = useParams();
  const [tab, setTab] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState<any>(false);
  const [isOperatorModalOpen, setOperatorIsModalOpen] = useState<any>(false);
  const [operatorOptions, setOperatorOptions] = useState<Option[]>([]);

  const { data: agentAction, isLoading } = useGetAgentAction();
  const { data: agentTypes, isLoading: isLoadingActionTypes } = useGetAgentTypeOptions();
  const { data: operator, isLoading: isOperatorLoading } = useGetOperators(currentPage);
  const { data: worksheets, isLoading: isWorksheetLoading } = useGetWorksheets();
  const { data: availableIntegrations } = useGetAvailableIntegrations();

  const { mutate: handleAgentAction, isPending } = useCreateOrUpdateAgentAction();

  const { handleSubmit, control, setValue, watch } = useForm<AgentActionRequest>({
    resolver: zodResolver(ActionFormSchema),
    defaultValues: (() => {
      const tempData = localStorage.getItem('tempData');
      return tempData
        ? JSON.parse(tempData)
        : {
            ...agentAction,
            action_details: {
              ...agentAction?.action_details,
              transfer_mode: 'cold',
              handoff_message: { ...agentAction?.action_details.handoff_message, type: 'prompt' },
              http_method: 'get',
              timeout: 10
            }
          };
    })(),
    values: agentAction
  });

  const targetType = watch('action_details.target_type');
  const transferMode = watch('action_details.transfer_mode');
  const { data: integrations } = useGetIntegrations({ 'filter[active_true]': true });

  useEffect(() => {
    if (transferMode === 'warm') {
      setValue('action_details.target_type', targetType || 'phone');
    } else {
      setValue('action_details.target_type', targetType || 'sip');
    }
  }, [transferMode, setValue, targetType]);

  const combinedData = useMemo(() => {
    return (
      integrations?.data?.map((item) => {
        const matchingIntegration = availableIntegrations?.data?.find((integration) => integration.code === item.code);
        return {
          ...item,
          description: matchingIntegration?.description,
          category: matchingIntegration?.category,
          sub_name: matchingIntegration?.name,
          small_avatar_url: matchingIntegration?.small_avatar_url,
          connection_type: matchingIntegration?.connection_type
        };
      }) ?? []
    );
  }, [availableIntegrations, integrations]);

  useEffect(() => {
    if (operator?.data) {
      setOperatorOptions((prevOptions) => {
        const existingIds = new Set(prevOptions.map((option) => option.value));
        const newOptions = operator.data
          .filter((item) => !existingIds.has(item.id))
          .map((item) => ({
            label: `${item.first_name} (${item.phone_number})`,
            value: item.id
          }));
        return [...prevOptions, ...newOptions];
      });
    }
  }, [operator]);

  const loadMoreOperator = () => {
    if (!isOperatorLoading && currentPage < (operator?.meta.total_pages || 1)) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const selectedIntegration = watch('action_details.integration_id');

  const {
    fields: headerFields,
    append: headerAppend,
    remove: headerRemove
  } = useFieldArray({
    control,
    name: 'action_details.headers'
  });

  const {
    fields: bodyParamsFields,
    append: bodyParamsAppend,
    remove: bodyParamsRemove
  } = useFieldArray({
    control,
    name: 'action_details.body_params'
  });

  const {
    fields: queryParamsFields,
    append: queryParamsAppend,
    remove: queryParamsRemove
  } = useFieldArray({
    control,
    name: 'action_details.query_params'
  });

  useEffect(() => {
    if (selectedIntegration !== agentAction?.action_details?.integration_id) {
      setValue('action_details.calendar_id', '');
    } else {
      setValue('action_details.calendar_id', agentAction?.action_details?.calendar_id);
    }
  }, [selectedIntegration, setValue, agentAction]);

  const onSubmit = (data: any) => {
    handleAgentAction(data);
  };

  const integrationOptions = combinedData.map((item) => ({
    label: (
      <Box
        sx={{
          minWidth: 250
        }}
        border='1px solid #ECECEC'
        py={0.5}
        px={1}
        borderRadius={1}
        display='flex'
        gap={1}
        alignItems='center'
      >
        <img
          src={item.small_avatar_url}
          alt={`${item.sub_name} icon`}
          style={{ height: '40px', width: '80px', objectFit: 'contain' }}
        />
        <Box display='flex' flexDirection='column'>
          <Typography fontSize={16} fontWeight={500} lineHeight={1} marginBottom={0.2}>
            {item.name}
          </Typography>
          <Typography fontSize={14} color='text.secondary' lineHeight={1}>
            {item.sub_name}
          </Typography>
        </Box>
      </Box>
    ),
    value: item.id!,
    disabled: !item.active
  }));

  const targetTypeOptions =
    watch('action_details.transfer_mode') === 'warm'
      ? [
          { label: 'Phone', value: 'phone' },
          { label: 'Extension', value: 'extension' }
        ]
      : [
          { label: 'SIP', value: 'sip' },
          { label: 'Phone', value: 'phone' }
        ];

  const selectedWorksheet = worksheets?.data?.find((item) => item.id === watch('action_details.worksheet_id'));

  if (isLoading) {
    return <Spinner />;
  }
  return (
    <>
      <Modal
        open={true}
        onClose={() =>
          navigate(`${urls.agentBase(workspaceId, agentId)}/${urls.home.configure}/${urls.configure.action}`)
        }
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box sx={style}>
          <Typography variant='h6'>Actions</Typography>
          <Box component='form' onSubmit={handleSubmit(onSubmit)} mt={2}>
            <Select
              name='action_type'
              disabled={isLoadingActionTypes}
              label='Action Type'
              control={control}
              options={
                agentTypes?.data
                  ? Object.entries(agentTypes?.data).map(([key, value]) => ({
                      label: value,
                      value: key
                    }))
                  : []
              }
            />
            <Typography fontSize='12px' color='secondary'>
              Select what type of action you need
            </Typography>
            <Input
              name='name'
              label='Name*'
              placeholder='Give this friendly name'
              control={control}
              fullWidth
              sx={{ marginTop: 2 }}
            />
            <Typography fontSize='12px' color='secondary'>
              Give this integration a name
            </Typography>
            {watch('action_type') === 'transfer_call' && (
              <>
                <Input
                  name='action_details.trigger'
                  label='Trigger'
                  placeholder='When customer ask to talk to a live representative'
                  control={control}
                  sx={{ marginTop: 2 }}
                  fullWidth
                />
                <Typography fontSize='12px' color='secondary'>
                  When should the transfer take place?
                </Typography>
                <Box bgcolor='#F0F0F0' mt={2} mb={2} borderRadius={2}>
                  <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)} aria-label='configure tabs'>
                    <Tab
                      sx={{ width: '50%' }}
                      label='Basic Settings'
                      {...a11yProps('actionSettings', 'actionSettingsPanel', 0)}
                    />
                    <Tab
                      sx={{ width: '50%' }}
                      label='Advance Settings'
                      {...a11yProps('actionSettings', 'actionSettingsPanel', 1)}
                    />
                  </Tabs>
                </Box>
                {tab === 0 ? (
                  <>
                    <Typography fontWeight={400} fontSize={16} marginTop={2} marginBottom={0.5}>
                      Transfer Mode
                    </Typography>
                    <Select
                      label=''
                      name='action_details.transfer_mode'
                      control={control}
                      options={[
                        { label: 'Cold Transfer', value: 'cold' },
                        { label: 'Warm Transfer', value: 'warm' }
                      ]}
                      onChange={(e) => {
                        setValue('action_details.transfer_mode', String(e.target.value));
                        setValue('action_details.target_type', '');
                        setValue('action_details.target_value', '');
                      }}
                    />
                    {/* {watch('action_details.transfer_mode') === 'warm' && (
                      <Box marginY={2} padding={2} border='1px solid #F3F3F3' borderRadius={3}>
                        <Typography>Handoff message</Typography>
                        <Radio
                          name='action_details.handoff_message.type'
                          control={control}
                          label=''
                          options={[
                            { label: 'Prompt', value: 'prompt' },
                            { label: 'Static Sentence', value: 'static_sentence' }
                          ]}
                        />
                        <Input
                          name='action_details.handoff_message.content'
                          multiline
                          minRows={3}
                          maxRows={3}
                          placeholder={
                            watch('action_details.handoff_message.type') === 'prompt'
                              ? 'Say hello to the agent and summarize the user problem to him'
                              : 'Enter static message'
                          }
                          control={control}
                          sx={{ marginTop: 2 }}
                          fullWidth
                        />
                      </Box>
                    )} */}
                    <Typography fontWeight={400} fontSize={16} marginTop={2} marginBottom={0.5}>
                      Target Type
                    </Typography>
                    <Select label='' name='action_details.target_type' control={control} options={targetTypeOptions} />
                    {targetType === 'sip' && (
                      <>
                        <Typography fontWeight={400} fontSize={16} marginTop={2} marginBottom={0.5}>
                          SIP URI
                        </Typography>
                        <Input
                          name='action_details.target_value'
                          placeholder='Enter SIP URI (e.g. sip:username@domain.com)'
                          control={control}
                          fullWidth
                        />
                      </>
                    )}

                    {targetType === 'phone' && (
                      <>
                        <Typography fontWeight={400} fontSize={16} marginTop={2} marginBottom={0.5}>
                          Phone
                        </Typography>
                        <Select
                          label=''
                          name='action_details.target_value'
                          isLoading={isOperatorLoading}
                          control={control}
                          options={operatorOptions}
                          onScrollEnd={loadMoreOperator}
                          handleAddNewOption={() => setOperatorIsModalOpen(true)}
                        />
                      </>
                    )}

                    {targetType === 'extension' && (
                      <>
                        <Box display='flex' flexDirection='row' marginTop={2} gap={0.5}>
                          <Typography fontWeight={400} fontSize={16} marginBottom={0.5}>
                            Extension
                          </Typography>
                          <Tooltip
                            title='The extension digits to dial when calling the transfer target'
                            arrow
                            placement='top'
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  lineHeight: 1.5
                                }
                              }
                            }}
                          >
                            <Box marginTop={0.3} fontSize={18}>
                              <IoMdInformationCircleOutline />
                            </Box>
                          </Tooltip>
                        </Box>
                        <Input name='action_details.target_value' placeholder='201' control={control} fullWidth />
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Box display='flex' flexDirection='row' alignContent='center' marginTop={2} gap={0.5}>
                      <Typography fontWeight={400} fontSize={16}>
                        Trying to reach the target
                      </Typography>
                      <Tooltip
                        title='This message the agent says before ringing the transfer target'
                        arrow
                        placement='top'
                        componentsProps={{
                          tooltip: {
                            sx: {
                              lineHeight: 1.5
                            }
                          }
                        }}
                      >
                        <Box marginTop={0.3} fontSize={18}>
                          <IoMdInformationCircleOutline />
                        </Box>
                      </Tooltip>
                    </Box>
                    <Input
                      name='action_details.transfer_initiation_message'
                      placeholder='Let me transfer you to a human. Please hold on.'
                      control={control}
                      sx={{ marginTop: 0.5 }}
                      fullWidth
                    />
                    <Box display='flex' flexDirection='row' alignContent='center' marginTop={2} gap={0.5}>
                      <Typography fontWeight={400} fontSize={16}>
                        Target answered message
                      </Typography>
                      <Tooltip
                        title='The message the agent says if the transfer target has answered the call and right before transferring the call and leaving the conversation'
                        arrow
                        placement='top'
                        componentsProps={{
                          tooltip: {
                            sx: {
                              lineHeight: 1.5
                            }
                          }
                        }}
                      >
                        <Box marginTop={0.3} fontSize={18}>
                          <IoMdInformationCircleOutline />
                        </Box>
                      </Tooltip>
                    </Box>
                    <Input
                      name='action_details.transfer_success_message'
                      placeholder='I am transferring you now. Goodbye!'
                      control={control}
                      sx={{ marginTop: 0.5 }}
                      fullWidth
                    />
                    <Box display='flex' flexDirection='row' alignContent='center' marginTop={2} gap={0.5}>
                      <Typography fontWeight={400} fontSize={16}>
                        Target didn't answer message
                      </Typography>
                      <Tooltip
                        title='The message the agent says in case the transfer target never picks up the phone call'
                        arrow
                        placement='top'
                        componentsProps={{
                          tooltip: {
                            sx: {
                              lineHeight: 1.5
                            }
                          }
                        }}
                      >
                        <Box marginTop={0.3} fontSize={18}>
                          <IoMdInformationCircleOutline />
                        </Box>
                      </Tooltip>
                    </Box>
                    <Input
                      name='action_details.transfer_failure_message'
                      placeholder="Sorry i couldn't transfer you. Would you like to continue the conversation?"
                      control={control}
                      sx={{ marginTop: 0.5 }}
                      fullWidth
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        paddingTop: 2
                      }}
                    >
                      <Typography variant='body1' fontWeight={500}>
                        Human Availability
                      </Typography>
                      <Switch name='action_details.human_availability_enabled' control={control} />
                    </Box>
                    {watch('action_details.human_availability_enabled') && (
                      <>
                        <Typography fontWeight={400} fontSize={16} marginTop={2}>
                          "Human not available" message
                        </Typography>
                        <Input
                          name='action_details.unavailable_message'
                          placeholder='John is not available at the moment. Please try again later.'
                          control={control}
                          sx={{ marginTop: 0.5 }}
                          fullWidth
                        />
                        <Typography fontSize={12} color='secondary'>
                          Mandatory to ensure a smooth experience when the human is not available.
                        </Typography>

                        <Box marginTop={2}>
                          <DataTable data={daysOfWeek} columns={columns(control)} isHideHeader={true} />
                        </Box>
                      </>
                    )}
                  </>
                )}
                {/* <Box mt={2}>
                  <Select
                    label='Target Number *'
                    name='action_details.target_id'
                    isLoading={isOperatorLoading}
                    control={control}
                    options={operatorOptions}
                    onScrollEnd={loadMoreOperator}
                    handleAddNewOption={() => setOperatorIsModalOpen(true)}
                  />
                </Box> */}
              </>
            )}
            {watch('action_type') === 'send_sms' && (
              <>
                <Input
                  name='action_details.trigger'
                  label='Trigger'
                  placeholder='When appointment is confirmed'
                  control={control}
                  sx={{ marginTop: 2 }}
                  fullWidth
                />
                <Typography fontSize='12px' color='secondary'>
                  When should this SMS be sent
                </Typography>
                <Input
                  name='action_details.message_content'
                  label='Message Content'
                  control={control}
                  placeholder='Team Spark: Thank you for confirming your appointment with us. We look forward to seeing you soon!'
                  fullWidth
                  multiline
                  minRows={4}
                  sx={{ marginTop: 2 }}
                />
                <Typography fontSize='12px' color='secondary'>
                  Keep your message clear and concise
                </Typography>
              </>
            )}

            {watch('action_type') === 'custom_api' && (
              <>
                <Input
                  name='action_details.trigger'
                  label='Trigger'
                  placeholder='When a customer asks about appointment availability'
                  control={control}
                  sx={{ marginTop: 2 }}
                  fullWidth
                />
                <Typography fontSize='12px' color='secondary'>
                  When should this API be called? For example: "When a customer asks about appointment availability"
                </Typography>
                <Input
                  name='action_details.exposed_parameters'
                  label='Exposed Parameters'
                  placeholder='eg: order_status, dispatched_at'
                  control={control}
                  sx={{ marginTop: 2 }}
                  fullWidth
                />
                <Input
                  name='action_details.base_url'
                  label='Base Url'
                  placeholder='https://api.example.com/v1/endpoint'
                  control={control}
                  sx={{
                    marginTop: 2,
                    '.MuiFormControl-root': {
                      borderRight: '1px solid #cecece'
                    }
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment
                          position='start'
                          sx={{
                            '.MuiInputBase-root': {
                              marginTop: '10px',
                              marginRight: '10px'
                            }
                          }}
                        >
                          <Select
                            control={control}
                            name='action_details.http_method'
                            label=''
                            options={[
                              { label: 'GET', value: 'get', selected: true },
                              { label: 'POST', value: 'post' },
                              { label: 'PUT', value: 'put' }
                            ]}
                            defaultValue='get'
                            variant='standard'
                            disableUnderline
                            sx={{
                              minWidth: 80
                            }}
                          />
                        </InputAdornment>
                      )
                    }
                  }}
                  fullWidth
                />
                <Accordion
                  sx={{
                    boxShadow: 'none',
                    border: '1px solid #F3F3F3',
                    marginTop: 2,
                    '::before': { background: 'transparent' }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}
                    aria-controls='panel1-content'
                    id='panel1-header'
                  >
                    <Box display='flex' flexDirection='column'>
                      <Typography variant='h6' fontSize={16} fontWeight={600}>
                        Headers
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {headerFields.map((field: any, index: number) => (
                      <Box
                        key={field.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          mb: 3
                        }}
                      >
                        <Input
                          name={`action_details.headers[${index}].field_name`}
                          label='Field name'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <Input
                          name={`action_details.headers[${index}].description`}
                          label='Description'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <Input
                          name={`action_details.headers[${index}].value`}
                          label='Value'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <IconButton color='error' onClick={() => headerRemove(index)}>
                          <MdDelete />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      type='button'
                      color='secondary'
                      size='small'
                      variant='outlined'
                      startIcon={<FaPlus />}
                      onClick={() => headerAppend({ field_name: '', description: '' })}
                      sx={{ display: 'flex', margin: 'auto', marginTop: 1 }}
                    >
                      Add Field
                    </Button>
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  sx={{
                    boxShadow: 'none',
                    border: '1px solid #F3F3F3',
                    marginTop: 2,
                    '::before': { background: 'transparent' }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}
                    aria-controls='panel1-content'
                    id='panel1-header'
                  >
                    <Box display='flex' flexDirection='column'>
                      <Typography variant='h6' fontSize={16} fontWeight={600}>
                        Body Params
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {bodyParamsFields.map((field: any, index: number) => (
                      <Box
                        key={field.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          mb: 3
                        }}
                      >
                        <Input
                          name={`action_details.body_params[${index}].field_name`}
                          label='Field Name'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <Input
                          name={`action_details.body_params[${index}].description`}
                          label='Description'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <IconButton color='error' onClick={() => bodyParamsRemove(index)}>
                          <MdDelete />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      type='button'
                      color='secondary'
                      size='small'
                      variant='outlined'
                      startIcon={<FaPlus />}
                      onClick={() => bodyParamsAppend({ field_name: '', description: '' })}
                      sx={{ display: 'flex', margin: 'auto', marginTop: 1 }}
                    >
                      Add Field
                    </Button>
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  sx={{
                    boxShadow: 'none',
                    border: '1px solid #F3F3F3',
                    marginTop: 2,
                    '::before': { background: 'transparent' }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}
                    aria-controls='panel1-content'
                    id='panel1-header'
                  >
                    <Box display='flex' flexDirection='column'>
                      <Typography variant='h6' fontSize={16} fontWeight={600}>
                        Query Params
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {queryParamsFields.map((field: any, index: number) => (
                      <Box
                        key={field.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          mb: 3
                        }}
                      >
                        <Input
                          name={`action_details.query_params[${index}].field_name`}
                          label='Field Name'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <Input
                          name={`action_details.query_params[${index}].description`}
                          label='Description'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <IconButton color='error' onClick={() => queryParamsRemove(index)}>
                          <MdDelete />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      type='button'
                      color='secondary'
                      size='small'
                      variant='outlined'
                      startIcon={<FaPlus />}
                      onClick={() => queryParamsAppend({ field_name: '', description: '' })}
                      sx={{ display: 'flex', margin: 'auto', marginTop: 1 }}
                    >
                      Add Field
                    </Button>
                  </AccordionDetails>
                </Accordion>
              </>
            )}
            {watch('action_type') === 'write_to_worksheet' && (
              <>
                <Input
                  name='action_details.trigger'
                  label='Trigger'
                  placeholder='When customer asks to leave a note'
                  control={control}
                  sx={{ marginTop: 2 }}
                  fullWidth
                />
                <Typography fontSize='12px' color='secondary'>
                  Define when to trigger this action
                </Typography>
                <Input
                  name='action_details.agent_response'
                  label='Agent Response'
                  placeholder='Let me take a note so that we can get back to you'
                  control={control}
                  sx={{ marginTop: 2 }}
                  fullWidth
                />
                <Box mt={2}>
                  <Select
                    label='Worksheet'
                    name='action_details.worksheet_id'
                    isLoading={isWorksheetLoading}
                    control={control}
                    options={worksheets?.data?.map((item) => ({ value: item.id, label: item.name })) ?? []}
                  />
                </Box>
                {watch('action_details.worksheet_id') && (
                  <>
                    <Typography mt={2} variant='subtitle1'>
                      Data Collect For
                    </Typography>
                    <Box display='flex' flexWrap='wrap' flexDirection='row'>
                      {selectedWorksheet?.columns?.length ? (
                        selectedWorksheet?.columns.map((column) => (
                          <Box key={column.id}>
                            <Controller
                              name='action_details.columns'
                              control={control}
                              defaultValue={[]}
                              render={({ field }) => (
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      value={column.name}
                                      checked={Array.isArray(field.value) && field.value.includes(column.name)}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        if (e.target.checked) {
                                          field.onChange([...(field.value || []), value]);
                                        } else {
                                          field.onChange((field.value || []).filter((v) => v !== value));
                                        }
                                      }}
                                    />
                                  }
                                  label={column.name}
                                />
                              )}
                            />
                          </Box>
                        ))
                      ) : (
                        <Typography variant='body2' color='textSecondary'>
                          No columns are added
                        </Typography>
                      )}
                      <Button
                        sx={{ textWrap: 'nowrap', minWidth: 'unset', background: 'white' }}
                        color='secondary'
                        onClick={() =>
                          navigate(
                            `add-column?worksheetId=${selectedWorksheet?.id}&position=${selectedWorksheet?.columns?.length ?? 0 + 1}`
                          )
                        }
                        variant='outlined'
                        startIcon={<GoPlus />}
                      >
                        Add Column
                      </Button>
                    </Box>
                  </>
                )}
                <Typography mt={2} variant='subtitle1'>
                  Notify Me Via
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                  <Controller
                    name='action_details.notify_via'
                    control={control}
                    defaultValue={[]}
                    render={({ field }) => {
                      const value = field.value || [];
                      return (
                        <>
                          <FormControlLabel
                            control={
                              <Checkbox
                                value='email'
                                checked={value.includes('email')}
                                onChange={(e) => {
                                  const checkboxValue = e.target.value;
                                  if (e.target.checked) {
                                    field.onChange([...value, checkboxValue]);
                                  } else {
                                    field.onChange(value.filter((v) => v !== checkboxValue));
                                  }
                                }}
                              />
                            }
                            label='Email'
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                value='sms'
                                checked={value.includes('sms')}
                                onChange={(e) => {
                                  const checkboxValue = e.target.value;
                                  if (e.target.checked) {
                                    field.onChange([...value, checkboxValue]); // Add value to array
                                  } else {
                                    field.onChange(value.filter((v) => v !== checkboxValue)); // Remove value from array
                                  }
                                }}
                              />
                            }
                            label='SMS/Text Message'
                          />
                        </>
                      );
                    }}
                  />
                </Box>

                <Box mt={2}>
                  <Select
                    label='Operator'
                    name='action_details.target_id'
                    isLoading={isOperatorLoading}
                    control={control}
                    options={operatorOptions}
                    onScrollEnd={loadMoreOperator}
                    handleAddNewOption={() => setOperatorIsModalOpen(true)}
                  />
                </Box>
              </>
            )}
            {/* {watch('action_type') === 'mcp_server' && (
              <>
                <Input
                  name='action_details.description'
                  label='Description'
                  placeholder='Description (describe the server capabilities)'
                  control={control}
                  fullWidth
                  sx={{ marginTop: 2 }}
                />
                <Typography fontSize='12px' color='secondary'>
                  Describe the server capabilities
                </Typography>
                <Input
                  name='action_details.server_url'
                  label='Server Url*'
                  placeholder='https://your-server-url.com/api'
                  control={control}
                  fullWidth
                  sx={{ marginTop: 2 }}
                />
                <Input
                  name='action_details.server_token'
                  label='Server Token'
                  placeholder='Secret Token*'
                  control={control}
                  fullWidth
                  sx={{ marginTop: 2 }}
                />
                <Input
                  name='action_details.timeout'
                  label='Timeout (seconds)'
                  type='number'
                  control={control}
                  fullWidth
                  sx={{ marginTop: 2 }}
                />
                <Accordion
                  sx={{
                    boxShadow: 'none',
                    border: '1px solid #F3F3F3',
                    marginTop: 2,
                    '::before': { background: 'transparent' }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}
                    aria-controls='panel1-content'
                    id='panel1-header'
                  >
                    <Box display='flex' flexDirection='column'>
                      <Typography variant='h6' fontSize={16} fontWeight={600}>
                        Headers
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {headerFields.map((field: any, index: number) => (
                      <Box
                        key={field.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          mb: 3
                        }}
                      >
                        <Input
                          name={`action_details.headers[${index}].field_name`}
                          label='Field name'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <Input
                          name={`action_details.headers[${index}].value`}
                          label='Value'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <IconButton color='error' onClick={() => headerRemove(index)}>
                          <MdDelete />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      type='button'
                      color='secondary'
                      size='small'
                      variant='outlined'
                      startIcon={<FaPlus />}
                      onClick={() => headerAppend({ field_name: '', description: '' })}
                      sx={{ display: 'flex', margin: 'auto', marginTop: 1 }}
                    >
                      Add Field
                    </Button>
                  </AccordionDetails>
                </Accordion>
              </>
            )} */}
            {watch('action_type') === 'integration' && (
              <>
                {/* <Input
                  name='action_details.trigger'
                  label='Trigger'
                  control={control}
                  sx={{ marginTop: 2 }}
                  fullWidth
                /> */}
                <Typography
                  color='#d97706'
                  bgcolor='#fffbeb'
                  padding={1.5}
                  fontSize={16}
                  borderRadius={3}
                  marginY={1.5}
                >
                  Note: Integrations are predefined flows and do not require a trigger configuration.
                </Typography>
                <Box mt={2}>
                  <Typography mb={2} color='secondary' variant='body1' fontSize={16} fontWeight={600}>
                    Select Integration
                  </Typography>
                  <Radio
                    name='action_details.integration_id'
                    control={control}
                    label=''
                    options={integrationOptions ?? []}
                  />
                </Box>
              </>
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'end',
                gap: '16px',
                marginTop: 2
              }}
            >
              <Button
                color='secondary'
                variant='outlined'
                sx={{ border: '1px solid #F3F3F3' }}
                onClick={() =>
                  navigate(`${urls.agentBase(workspaceId, agentId)}/${urls.home.configure}/${urls.configure.action}`)
                }
              >
                Cancel
              </Button>
              <Button type='submit' variant='contained' isPending={isPending}>
                {actionId ? 'Update' : 'Create'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <IntegrationForm open={isModalOpen} handleClose={() => setIsModalOpen(false)} />
      <OperatorForm isOpen={isOperatorModalOpen} handleClose={() => setOperatorIsModalOpen(false)} />
      <Outlet />
    </>
  );
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  border: '1px solid #F3F3F3',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px',
  maxHeight: '90vh',
  overflow: 'auto',
  width: {
    xs: '90%',
    sm: 400,
    md: 600,
    lg: 700
  }
};

const columns = (control: any): ColumnDef<any>[] => [
  {
    id: 'day',
    accessorKey: 'label',
    cell: (info) => (
      <Box display='flex'>
        <Switch name={`action_details.availability_schedule.[${info.row.original.value}].open`} control={control} />
        <Typography marginTop={1}>{info.row.original.label}</Typography>
      </Box>
    )
  },
  {
    id: 'time',
    cell: (info: any) => (
      <Box display='flex' gap={1}>
        <TimePicker
          name={`action_details.availability_schedule.[${info.row.original.value}].start_time`}
          control={control}
        />
        <TimePicker
          name={`action_details.availability_schedule.[${info.row.original.value}].end_time`}
          control={control}
        />
      </Box>
    )
  }
];

const daysOfWeek = [
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Saturday', value: 'saturday' },
  { label: 'Sunday', value: 'sunday' }
];
